import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useOrder } from "../context/OrderContext";
import Stepper from "../components/Stepper";
import CouponInput from "../components/CouponInput";
import { computePricing } from "../utils/couponCalculations";
import { sendBookingNotificationEmail } from "../lib/emailService";
import type { Coupon } from "../hooks/useCoupon";

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { draft, computeTotal, resetOrder, goToStep, canGoToStep } = useOrder();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);

  // Calcul du pricing avec/sans coupon
  const baseServicePrice = draft.service?.base_price ?? 0;
  const optionsPrice = draft.option?.price_delta ?? 0;
  const transportPrice = (draft.pickupPrice ?? 0) + (draft.deliveryPrice ?? 0);

  const pricing = computePricing({
    baseServicePrice,
    optionsPrice: optionsPrice + transportPrice,
    coupon: appliedCoupon
      ? {
          type: appliedCoupon.type,
          partner_commission_pct: appliedCoupon.partner_commission_pct,
          member_discount_pct: appliedCoupon.member_discount_pct,
          user_discount_pct: appliedCoupon.user_discount_pct,
        }
      : null,
    mode: "PUBLIC",
  });

  const total = pricing.final_total;

  useEffect(() => {
    if (!draft.deliveryMode || !canGoToStep("PAYMENT")) {
      navigate("/order/delivery");
      return;
    }
    goToStep("PAYMENT");
  }, [draft.deliveryMode, canGoToStep, goToStep, navigate]);

  const handleCouponApplied = (coupon: Coupon | null) => {
    setAppliedCoupon(coupon);
  };

  const handleConfirmOrder = async () => {
    if (!user) {
      alert("Merci de te connecter avant de valider la commande.");
      navigate("/auth/login", {
        state: { from: { pathname: "/order/payment" } },
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1️⃣ Création de la commande
      const { data: order, error: orderErr } = await supabase
        .from("orders")
        .insert({
          user_id: user.id,
          service_id: draft.service?.id,
          base_price: draft.service?.base_price,
          racket_brand: draft.racketBrand,
          racket_description: draft.racketDescription,
          tension_vertical: draft.tensionVertical,
          tension_horizontal: draft.tensionHorizontal,

          pickup_mode_id: draft.pickupMode?.id,
          pickup_address: draft.pickupAddress || profile?.address_line1,
          pickup_distance_km: draft.pickupDistanceKm ?? null,
          pickup_price: draft.pickupPrice ?? null,

          delivery_mode_id: draft.deliveryMode?.id,
          delivery_address: draft.deliveryAddress ?? profile?.address_line1,
          delivery_distance_km: draft.deliveryDistanceKm ?? null,
          delivery_price: draft.deliveryPrice ?? null,

          total_price: total,
          status: "EN_ATTENTE",
          payment_status: "OFFLINE_PENDING",

          // Coupon USER (si appliqué)
          coupon_id: appliedCoupon?.id ?? null,
          partner_id: appliedCoupon?.partner_id ?? null,
          original_service_price: appliedCoupon ? baseServicePrice : null,
          user_discount_amount: pricing.discount_amount,
          partner_commission_amount: pricing.commission_amount,
        })
        .select()
        .single();

      if (orderErr) throw orderErr;

      const orderId = order.id;

      // 2️⃣ Insertion des options dans order_options
      if (draft.option) {
        const { error: optErr } = await supabase.from("order_options").insert({
          order_id: orderId,
          option_id: draft.option.id,
          quantity: 1,
        });

        if (optErr) throw optErr;
      }

      // 2.5️⃣ Incrémenter le compteur d'utilisation du coupon (si utilisé)
      if (appliedCoupon?.id) {
        await supabase
          .rpc("increment_coupon_usage", { coupon_uuid: appliedCoupon.id })
          .then(({ error }) => {
            if (error) console.error("Erreur increment coupon usage:", error);
          });
      }

      // 3️⃣ Envoi de l'email de notification
      try {
        await sendBookingNotificationEmail({
          orderId,
          orderDetails: {
            created_at: order.created_at,
            total_price: order.total_price,
            status: order.status,
            racket_brand: order.racket_brand,
            racket_description: order.racket_description,
            tension_vertical: order.tension_vertical,
            tension_horizontal: order.tension_horizontal,
            pickup_address: order.pickup_address,
            delivery_address: order.delivery_address,
            pickup_distance_km: order.pickup_distance_km,
            delivery_distance_km: order.delivery_distance_km,
            pickup_price: order.pickup_price,
            delivery_price: order.delivery_price,
            service_name: draft.service?.name,
            service_base_price: draft.service?.base_price,
            pickup_mode_label: draft.pickupMode?.label,
            delivery_mode_label: draft.deliveryMode?.label,
            options: draft.option ? [{
              label: draft.option.label,
              price_delta: draft.option.price_delta
            }] : [],
            // Infos coupon pour l'email
            coupon_code: appliedCoupon?.code ?? undefined,
            original_service_price: appliedCoupon ? baseServicePrice : undefined,
            discount_amount: pricing.discount_amount > 0 ? pricing.discount_amount : undefined,
          },
          customerDetails: {
            full_name: profile?.full_name,
            email: user.email,
            phone: profile?.phone,
            address_line1: profile?.address_line1,
            city: profile?.city
          }
        });
      } catch (emailError) {
        // On ne bloque pas la commande si l'email échoue
        console.error("Erreur lors de l'envoi de l'email de notification:", emailError);
      }

      // 4️⃣ OK
      setSuccess(true);
      resetOrder();
      navigate(`/order/confirmation?orderId=${orderId}`);
    } catch (e: any) {
      console.error("Erreur lors de la création de commande:", e);
      setError(
        "Impossible de valider la commande. Merci de réessayer plus tard."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <Stepper />

      <div className="card-header">
        <h1 className="card-title">Confirmation de la commande</h1>
      </div>

      {/* Récapitulatif */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 600 }}>Récapitulatif</h2>
        <ul style={{ fontSize: ".9rem", color: 'var(--tun-card-text)' }}>
          <li>
            <strong>Service :</strong> {draft.service?.name}{' '}
            {appliedCoupon && pricing.discount_amount > 0 ? (
              <>
                (<s style={{ color: '#9ca3af' }}>{baseServicePrice.toFixed(2)} CHF</s>{' '}
                <span style={{ color: '#22c55e', fontWeight: 600 }}>{pricing.final_service_price.toFixed(2)} CHF</span>)
              </>
            ) : (
              <>({draft.service?.base_price?.toFixed(2)} CHF)</>
            )}
          </li>

          {draft.option && (
            <li>
              <strong>Option :</strong> {draft.option.label} (
              {draft.option.price_delta.toFixed(2)} CHF)
            </li>
          )}

          <li>
            <strong>Raquette :</strong> {draft.racketBrand}
            {draft.racketDescription && ` — ${draft.racketDescription}`}
          </li>

          <li>
            <strong>Tension :</strong> {draft.tensionVertical} /{" "}
            {draft.tensionHorizontal} kg
          </li>

          <li>
            <strong>Prise en charge :</strong> {draft.pickupMode?.label}
            {draft.pickupPrice != null &&
              ` — ${draft.pickupPrice.toFixed(2)} CHF`}
          </li>

          <li>
            <strong>Livraison :</strong> {draft.deliveryMode?.label}
            {draft.deliveryPrice != null &&
              ` — ${draft.deliveryPrice.toFixed(2)} CHF`}
          </li>
        </ul>
      </div>

      {/* Coupon utilisateur */}
      <div style={{ marginBottom: "1.5rem" }}>
        <CouponInput onCouponApplied={handleCouponApplied} />

        {/* Badge partenaire si coupon lié à un partner */}
        {appliedCoupon?.partner && (
          <div
            data-testid="coupon-partner-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              marginTop: "0.5rem",
              padding: "0.3rem 0.7rem",
              background: `${(appliedCoupon.partner as any).color_code}15`,
              border: `1px solid ${(appliedCoupon.partner as any).color_code}40`,
              borderRadius: "9999px",
              fontSize: ".8rem",
              color: (appliedCoupon.partner as any).color_code,
              fontWeight: 500,
            }}
          >
            🏢 {(appliedCoupon.partner as any).name}
          </div>
        )}

        {/* Résumé réduction si coupon appliqué */}
        {appliedCoupon && pricing.discount_amount > 0 && (
          <div
            data-testid="coupon-discount-summary"
            style={{
              marginTop: "0.5rem",
              padding: "0.75rem 1rem",
              background: "rgba(34, 197, 94, 0.08)",
              border: "1px solid rgba(34, 197, 94, 0.3)",
              borderRadius: "0.5rem",
              fontSize: ".85rem",
              color: "#22c55e",
            }}
          >
            Réduction de <strong>{pricing.discount_amount.toFixed(2)} CHF</strong> sur le service
            {" "}(économie de {((pricing.discount_amount / baseServicePrice) * 100).toFixed(0)}%)
          </div>
        )}
      </div>

      {/* TOTAL */}
      <div
        data-testid="payment-total"
        style={{
          borderTop: "1px solid rgba(148,163,184,0.3)",
          paddingTop: "1rem",
          fontSize: "1.1rem",
          fontWeight: 700,
          color: "#22c55e",
        }}
      >
        Total à payer : {total.toFixed(2)} CHF
      </div>

      {/* Prix original barré si réduction */}
      {appliedCoupon && pricing.discount_amount > 0 && (
        <div data-testid="price-before-after" style={{ fontSize: '.85rem', color: '#9ca3af', marginTop: '0.25rem' }}>
          <s>{(baseServicePrice + optionsPrice + transportPrice).toFixed(2)} CHF</s>
          {' → '}{total.toFixed(2)} CHF
          {' '}(économie de {pricing.discount_amount.toFixed(2)} CHF)
        </div>
      )}

      {/* Paiement offline */}
      <div style={{ marginTop: "1.5rem" }}>
        <div
          data-testid="payment-reassurance"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.75rem",
            padding: "0.875rem 1rem",
            background: "rgba(34,197,94,0.08)",
            border: "1px solid rgba(34,197,94,0.3)",
            borderRadius: "8px",
            marginBottom: "1.25rem",
          }}
        >
          <span style={{ fontSize: "1.1rem", lineHeight: 1.4, flexShrink: 0 }}>✓</span>
          <p style={{ margin: 0, color: "#86efac", fontWeight: 500, lineHeight: 1.5 }}>
            Vous payez uniquement à la livraison ou à la remise de votre raquette.
            <span style={{ display: "block", fontWeight: 400, fontSize: ".85rem", color: "#9ca3af", marginTop: "0.2rem" }}>
              Aucune carte bancaire requise — le règlement se fait directement avec le cordeur.
            </span>
          </p>
        </div>

        <button
          data-testid="confirm-order-button"
          className="btn btn-primary"
          onClick={handleConfirmOrder}
          disabled={loading}
          style={{ marginTop: "1rem" }}
        >
          {loading ? "Validation…" : "Confirmer ma commande"}
        </button>
      </div>

      {error && (
        <p style={{ color: "#f87171", marginTop: "1rem" }}>{error}</p>
      )}

      {success && (
        <p style={{ color: "#22c55e", marginTop: "1rem" }}>
          ✔ Commande enregistrée !
        </p>
      )}
    </div>
  );
};

export default PaymentPage;
